import { useCallback, useEffect, useState } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { transformKeys } from '../services/transformers';
import type { CallHistoryItem } from '../types';

export type CallDetailObserverEvent = Partial<CallHistoryItem> & { id: string };

interface UseCallDetailObserverOptions {
  enabled?: boolean;
  onEvent?: (event: CallDetailObserverEvent) => void;
}

interface UseCallDetailObserverReturn {
  isConnected: boolean;
  isSubscribed: boolean;
}

/**
 * Subscribe to call detail updates for a single call id.
 */
export function useCallDetailObserver(
  callId?: string | null,
  options: UseCallDetailObserverOptions = {},
): UseCallDetailObserverReturn {
  const { enabled = true, onEvent } = options;
  const { state, addObserver, removeObserver } = useWebSocket();
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleEvent = useCallback(
    (data: CallDetailObserverEvent, action: string) => {
      if (action !== 'update' && action !== 'create') {
        return;
      }

      const normalized = transformKeys<CallDetailObserverEvent>(data);
      onEvent?.(normalized);
    },
    [onEvent],
  );

  useEffect(() => {
    if (!enabled || !callId || !state.isConnected) {
      setIsSubscribed(false);
      return;
    }

    const observerId = addObserver(
      'call',
      { data: { call_id: callId } },
      handleEvent,
      {
        subscribeAction: 'subscribe',
        unsubscribeAction: 'unsubscribe',
        includeCreateEvents: true,
      },
    );
    setIsSubscribed(true);

    return () => {
      removeObserver(observerId);
      setIsSubscribed(false);
    };
  }, [enabled, callId, state.isConnected, addObserver, removeObserver, handleEvent]);

  return {
    isConnected: state.isConnected,
    isSubscribed,
  };
}

export default useCallDetailObserver;
